import { auth, db, storage } from "./firebase";
import {
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  runTransaction,
  setDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Auth functions
export const logoutUser = () => signOut(auth);

export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error("Error signing in with Google", error);
    throw error;
  }
};

// Generic Firestore functions
export const addDocument = async <T extends { [key: string]: any }>(
  collectionName: string,
  data: T
) => {
  const docRef = await addDoc(collection(db, collectionName), data);
  return { id: docRef.id, ...data };
};

interface FirebaseDoc {
  id: string;
  userId?: string;
  [key: string]: any;
}

export const getDocuments = async (collectionName: string): Promise<FirebaseDoc[]> => {
  const querySnapshot = await getDocs(collection(db, collectionName));
  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

export const updateDocument = async (collectionName: string, documentId: string, data: any) => {
  const docRef = doc(db, collectionName, documentId);
  await setDoc(docRef, data, { merge: true });
  return {
    id: documentId,
    ...data
  };
};

export const deleteDocument = async (collectionName: string, documentId: string) => {
  const docRef = doc(db, collectionName, documentId);
  await deleteDoc(docRef);
};

export const getDocument = async (collectionName: string, documentId: string) => {
  const docRef = doc(db, collectionName, documentId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return {
    id: docSnap.id,
    ...docSnap.data()
  };
};

// Storage functions
export const uploadFile = async (path: string, file: File): Promise<string> => {
  try {
    // Create a storage reference
    const storageRef = ref(storage, path);
    
    // Upload the file
    const uploadResult = await uploadBytes(storageRef, file);
    
    // Get the download URL
    const downloadUrl = await getDownloadURL(uploadResult.ref);
    
    return downloadUrl;
  } catch (error) {
    console.error('Error in uploadFile:', error);
    throw error;
  }
};

export async function getCustomerMaps(customerId: string) {
  try {
    const mapsRef = collection(db, 'maps');
    // Try both possible field names
    const q = query(mapsRef, 
      where('customer.id', '==', customerId)
    );
    const querySnapshot = await getDocs(q);
    
    const maps = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // If no results, try with userId field
    if (maps.length === 0) {
      const q2 = query(mapsRef, where('userId', '==', customerId));
      const querySnapshot2 = await getDocs(q2);
      return querySnapshot2.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
    
    return maps;
  } catch (error) {
    console.error('Error fetching customer maps:', error);
    throw error;
  }
}

